/**
 * Handles submission for the contact page form with reCAPTCHA v3 support.
 */
export const initPageContactForm = (): void => {
  const formWrapper = document.querySelector<HTMLDivElement>(
    ".contact-form-column .form-wrapper",
  );
  if (!formWrapper) return;

  const form = formWrapper.querySelector<HTMLFormElement>(".app-contact-form");
  if (!form) return;

  const messagesContainer = formWrapper.querySelector<HTMLDivElement>(
    ".form-messages-container",
  );

  if (!messagesContainer) {
    console.warn(
      "[Contact Form] Required message container element not found.",
    );
    return;
  }

  const successTpl =
    window.themeVariables?.formTranslations?.contactForm?.status
      ?.successMessage || "<strong>Success!</strong> Message sent.";
  const errorTpl =
    window.themeVariables?.formTranslations?.contactForm?.status
      ?.errorMessage || "<strong>Error!</strong> Message not sent.";

  const createAlert = (type: "success" | "error", message: string): string => {
    const icon = type === "success" ? "#check" : "#close";
    return `
      <div class="alert alert--${type} alert--filled">
        <div class="alert__icon">
          <svg class="icon"><use xlink:href="${icon}"></use></svg>
        </div>
        <p class="alert__text">${message}</p>
        <span class="alert__close">
          <svg class="icon"><use xlink:href="#close"></use></svg>
        </span>
      </div>
    `;
  };

  /**
   * Performs the actual AJAX submission
   */
  const submitForm = async (token?: string) => {
    const submitButton = form.querySelector<HTMLButtonElement>(
      'button[type="submit"]',
    );
    if (!submitButton) return;

    const formData = new FormData(form);
    formData.append("action", "submit_page_contact_form");

    if (window.themeVariables?.pageContactFormNonce) {
      formData.append(
        "contactFormNonce",
        window.themeVariables.pageContactFormNonce,
      );
    }

    if (token) {
      formData.append("recaptchaToken", token);
    }

    try {
      const response = await fetch(window.themeVariables!.ajaxUrl, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        const message = result.data?.message || successTpl;
        messagesContainer.innerHTML = createAlert("success", message);
        form.style.display = "none";
      } else {
        const message = result.data?.message || errorTpl;
        messagesContainer.innerHTML = createAlert("error", message);
        submitButton.disabled = false;
        submitButton.style.opacity = "1";
      }
    } catch (error) {
      console.error("Form submission error:", error);
      messagesContainer.innerHTML = createAlert("error", errorTpl);
      submitButton.disabled = false;
      submitButton.style.opacity = "1";
    }

    const closeBtn = messagesContainer.querySelector(".alert__close");
    if (closeBtn) {
      closeBtn.addEventListener(
        "click",
        () => {
          messagesContainer.innerHTML = "";
        },
        { once: true },
      );
    }
  };

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const submitButton = form.querySelector<HTMLButtonElement>(
      'button[type="submit"]',
    );
    if (!submitButton) return;

    messagesContainer.innerHTML = "";
    submitButton.disabled = true;
    submitButton.style.opacity = "0.7";

    const siteKey = window.themeVariables?.recaptchaSiteKey;

    // @ts-ignore: grecaptcha is global from script tag
    if (siteKey && typeof grecaptcha !== "undefined") {
      // @ts-ignore
      grecaptcha.ready(() => {
        // @ts-ignore
        grecaptcha
          .execute(siteKey, { action: "submit_page_contact" })
          .then((token: string) => {
            submitForm(token);
          });
      });
    } else {
      // Fallback if reCAPTCHA is not loaded or configured
      submitForm();
    }
  });

  console.log("[Forms] Page Contact Form initialized.");
};
